import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { BankCountry, BanksPage } from '../../services/interfaces/bank.interface';
import { useAppSelector } from '../../app/hooks';
import { selectConfiguration } from '../../app/slices/configuration.slice';
import { BankIntegrationVendor } from '../../types/bank-integration-vendor';
import { getBankService } from '../../services/service.factory';
import { useCallback } from 'react';

const options: SWRInfiniteConfiguration = {
	revalidateIfStale: false,
	revalidateOnFocus: false,
	revalidateOnReconnect: false,
	initialSize: 1,
	revalidateAll: false,
	revalidateFirstPage: false,
	persistSize: false // extremely important:
	// reset the page size to 1 when the first page's key changes
};

type PageData = BanksPage;

export function useBankPages(country: BankCountry, search: string = '') {
	const getPageKey = getKey(country, search);
	const configuration = useAppSelector(selectConfiguration);
	const bankFetcher = useCallback(fetcher(configuration.bankIntegrationVendor), [
		configuration.bankIntegrationVendor
	]);
	const { data, error, isLoading, isValidating, size, setSize } = useSWRInfinite<PageData>(
		getPageKey,
		bankFetcher,
		options
	);

	const hasNext = data && isLastPage(data);
	return {
		pages: data,
		error,
		isLoading,
		isValidating,
		size,
		setSize,
		hasNext
	};
}

const isLastPage = (data: Array<BanksPage>) => data[data.length - 1]?.pagination.next;

const getKey = (country: BankCountry, search: string) => {
	return (page: number, previousPageData: BanksPage) => {
		if (!previousPageData) return ['/api/banks', country, search];
		const { next } = previousPageData.pagination;
		if (!next) return;
		const offset = next.offset;
		return ['/api/banks', country, search, offset];
	};
};

type BanksFetcherArgs = [string, string, string, number];
const fetcher: (bankIntegrationVendor: BankIntegrationVendor) => (args: BanksFetcherArgs) => Promise<BanksPage> =
	(vendor) => async (args) => {
		const country = args[1];
		const search_term = args[2];
		const offset = args[3] || undefined;
		return await getBankService(vendor).fetchBanks({
			country,
			searchTerm: search_term,
			paginationOffset: offset,
			paginationSize: DEFAULT_PAGE_SIZE
		});
	};

export const DEFAULT_PAGE_SIZE = 8;
